import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext.hook';
import { useToast } from '@/hooks/use-toast';
import { suitabilityKeys } from '@/lib/query-keys';
import {
  CURRENT_SUITABILITY_VERSION,
  calculateSuitabilityScore,
  classifyProfile,
} from '@/constants/suitability';
import type {
  InvestorProfileType,
  SuitabilityAnswers,
  SuitabilityValidation,
} from '@/types/suitability';

interface UseSuitabilityReturn {
  /** true se o investidor precisa responder (não tem ou expirou) */
  needsAssessment: boolean;
  /** Carregando verificação inicial */
  checking: boolean;
  /** Submetendo respostas */
  submitting: boolean;
  /** Perfil atual (se existir e válido) */
  currentProfile: InvestorProfileType | null;
  /** Data de expiração do questionário atual */
  expiresAt: string | null;
  /** Submeter o questionário */
  submitQuestionnaire: (answers: SuitabilityAnswers) => Promise<{
    profileType: InvestorProfileType;
    score: number;
  } | null>;
}

async function fetchIpAddress(): Promise<string> {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data.ip || 'unknown';
  } catch {
    return 'unknown';
  }
}

export function useSuitability(): UseSuitabilityReturn {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const userId = user?.id;
  const userRole = profile?.role;

  // Apenas investidores precisam de suitability
  const isInvestor = userRole === 'investidor';

  const { data: validation, isLoading: checking } = useQuery({
    queryKey: suitabilityKeys.validation(userId),
    queryFn: async (): Promise<SuitabilityValidation | null> => {
      if (!userId) return null;

      const { data, error } = await supabase
        .rpc('check_suitability_valid', { p_user_id: userId });

      if (error) {
        console.error('Error checking suitability:', error);
        return null;
      }

      // RPC retorna array — pegamos o primeiro resultado
      const result = Array.isArray(data) ? data[0] : data;
      return result || null;
    },
    enabled: !!userId && isInvestor,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });

  const needsAssessment = isInvestor && !checking && (!validation || !validation.is_valid);

  const currentProfile = validation?.is_valid ? validation.profile_type : null;
  const expiresAt = validation?.is_valid ? validation.expires_at : null;

  const submitQuestionnaire = useCallback(async (answers: SuitabilityAnswers) => {
    if (!userId) return null;

    setSubmitting(true);

    try {
      const score = calculateSuitabilityScore(answers);
      const profileType = classifyProfile(score);
      const ipAddress = await fetchIpAddress();

      const { error } = await supabase
        .from('suitability_questionnaires')
        .insert({
          user_id: userId,
          profile_type: profileType,
          total_score: score,
          answers: answers as unknown as Record<string, unknown>,
          questionnaire_version: CURRENT_SUITABILITY_VERSION,
          birth_date: answers.birthDate || null,
          occupation: answers.occupation || null,
          monthly_income_range: answers.monthlyIncomeRange,
          net_worth_range: answers.netWorthRange,
          is_pep: answers.isPep ?? false,
          ip_address: ipAddress,
          user_agent: navigator.userAgent,
        });

      if (error) {
        console.error('Error submitting suitability:', error);
        toast({
          title: 'Erro ao salvar perfil',
          description: error.message,
          variant: 'destructive',
        });
        return null;
      }

      // Invalidar cache para refletir novo estado
      await queryClient.invalidateQueries({ queryKey: suitabilityKeys.validation(userId) });

      toast({
        title: 'Perfil de investidor definido!',
        description: `Seu perfil foi classificado como ${profileType}.`,
      });

      return { profileType, score };
    } catch (err) {
      console.error('Error submitting suitability:', err);
      toast({
        title: 'Erro inesperado',
        description: 'Não foi possível salvar seu perfil. Tente novamente.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [userId, toast, queryClient]);

  return {
    needsAssessment,
    checking,
    submitting,
    currentProfile,
    expiresAt,
    submitQuestionnaire,
  };
}
